import { Dimensions, Platform } from "react-native";
import * as Device from "expo-device";
import * as SecureStore from "expo-secure-store";
import { ProductCategory } from "./constants";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";
import { DefaultTheme } from "styled-components/native";
import {
  decryptStringFromStorage,
  ENCRYPTION_PREFIX,
  encryptStringForStorage,
  removeLegacyMasterKey,
} from "./secureCrypto";

const { deviceWidth: width } = useIsFoldable();
const { height } = Dimensions.get("window");
const DEVICE_SIZE = { width, height };
const SYSTEM_PLATFORM =
  Platform.OS === "web"
    ? 0
    : Platform.OS === "ios" && Device?.osName === "iOS"
    ? 1
    : Platform.OS === "ios" && Device?.osName === "iPadOS"
    ? 2
    : Platform.OS === "android"
    ? 3
    : 4; //To be reviewed

const SYSTEM_UI = (theme: DefaultTheme) => {
  if (theme.isLargeScreen) {
    return 0;
  } else if (theme.isMobile) {
    return 1;
  } else {
    return 2;
  }
};

const PLATFORM = Platform.OS === "web" ? 0 : 1;

const LEGACY_PASSWORD_KEY = "password";
const ENCRYPTED_PASSWORD_KEY = "userkey";

const readRawStorageValue = async (key: string) => {
  if (Platform.OS === "web") {
    return AsyncStorage.getItem(key);
  }
  return SecureStore.getItemAsync(key);
};

const writeRawStorageValue = async (key: string, value: string) => {
  if (Platform.OS === "web") {
    return AsyncStorage.setItem(key, value);
  }
  return SecureStore.setItemAsync(key, value);
};

const saveInSecureStore = async (params: any) => {
  const arrayedParam = Object.entries(params);

  await Promise.all(
    arrayedParam.map(async (param) => {
      if (!param[1] || typeof param[1] !== "string") return;

      const [key, value] = param;
      if (Platform.OS !== "web") {
        await writeRawStorageValue(key, value);
        return;
      }

      if (key === LEGACY_PASSWORD_KEY) {
        const legacyPassword = await readRawStorageValue(LEGACY_PASSWORD_KEY);
        if (legacyPassword) return;

        const encryptedPassword = await encryptStringForStorage(value);
        await writeRawStorageValue(ENCRYPTED_PASSWORD_KEY, encryptedPassword);
        return;
      }

      await writeRawStorageValue(key, value);
    })
  );
};

const getFromSecureStore = async (key: string) => {
  const storedValue = (await readRawStorageValue(key)) || "";

  if (Platform.OS !== "web") return storedValue;
  if (key !== LEGACY_PASSWORD_KEY) return storedValue;
  if (storedValue) {
    if (storedValue.startsWith(ENCRYPTION_PREFIX)) {
      return (await decryptStringFromStorage(storedValue)) || "";
    }
    return storedValue;
  }

  const encryptedPassword =
    (await readRawStorageValue(ENCRYPTED_PASSWORD_KEY)) || "";
  if (!encryptedPassword) return "";
  return (await decryptStringFromStorage(encryptedPassword)) || "";
};

const removeSecureStoreItems = async (params: any) => {
  if (!params.length) return;

  if (Platform.OS === "web") {
    params.map(async (param: string) => {
      await AsyncStorage.removeItem(param);
    });
  } else {
    params.map(async (param: string) => {
      await SecureStore.deleteItemAsync(param);
    });
    return;
  }

  const keysToRemove = params.flatMap((param) => {
    if (param === LEGACY_PASSWORD_KEY) {
      return [LEGACY_PASSWORD_KEY, ENCRYPTED_PASSWORD_KEY];
    }
    return [param];
  });

  keysToRemove.map(async (param) => {
    await AsyncStorage.removeItem(param);
  });

  if (keysToRemove.includes(LEGACY_PASSWORD_KEY)) {
    await removeLegacyMasterKey();
  }
};

const productNameToTokenCategory = (name: ProductCategory) => {
  let tokenCat = "";
  switch (name) {
    case ProductCategory.JEWELRY:
    case ProductCategory.LABGROWN_JEWELRY:
      tokenCat = "jewelry";
      break;
    case ProductCategory.LAB_GROWN_DIAMOND:
      tokenCat = "lab";
      break;
    case ProductCategory.GEMSTONE:
      tokenCat = "gem";
      break;
    case ProductCategory.DIAMOND:
      tokenCat = "diam";
      break;
    default:
      tokenCat = "diam";
      break;
  }
  return tokenCat;
};

const checkAndroidVersion15 = () => {
  if (Platform.OS === "android") {
    const androidVersion = parseInt(Device?.osVersion ?? "0", 10);

    if (androidVersion === 15) {
      return true;
    }
  }

  return false;
};

const fetchLocalStorageItems = async (params: any) => {
  if (!params.length) return;

  return AsyncStorage.getItem(params);
};

const getProductAttributes = (
  allProducts: any,
  selectedProductId: string,
  selectedTheme: any
) => {
  const productKeys: Record<string, { key: string; themeKey: string }> = {
    "0": { key: "diamond", themeKey: "diam-06" },
    "1": { key: "lab_grown_diamond", themeKey: "lab-06" },
    "2": { key: "gemstone", themeKey: "gem-06" },
    "3": { key: "jewelry", themeKey: "jewelry-06" },
    "4": { key: "lab_grown_jewelry", themeKey: "jewelry-06" },
  };

  const defaultKey = { key: "jewelry", themeKey: "jewelry-02" };
  const { key, themeKey } = productKeys[selectedProductId] || defaultKey;

  const product = allProducts.find((item: any) => item?.key === key);

  return {
    color: selectedTheme[themeKey],
    text: product?.label,
    icon: product?.icon,
  };
};

export {
  DEVICE_SIZE,
  SYSTEM_PLATFORM,
  SYSTEM_UI,
  PLATFORM,
  saveInSecureStore,
  getFromSecureStore,
  removeSecureStoreItems,
  productNameToTokenCategory,
  checkAndroidVersion15,
  getProductAttributes,
  fetchLocalStorageItems,
};
